import * as THREE from 'three';
// @ts-ignore // Suppress type error for animejs default import
import * as anime from 'animejs';
// Import the OrbitControls class
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

// Define the structure for camera state
export interface CameraState {
  position: [number, number, number];
  target: [number, number, number];
}

// Use the correct type for anime timeline instance
// @ts-ignore - Suppress incorrect type error for timeline
type AnimeTimelineInstance = ReturnType<typeof anime.timeline>;

// Type alias for mesh groups for clarity
type MeshGroupType = { [key: string]: THREE.Mesh[] };

// --- NEW Keyframe Object Structure Interface ---
export interface KeyframeObject {
  camera: {
    position: { x: number; y: number; z: number; };
  };
  controls: {
    target: { x: number; y: number; z: number; };
  };
  // Can add duration/delay per keyframe later if needed
}

// --- Utility to get bounding box center of meshes ---
const getGroupCenter = (meshes: THREE.Mesh[]): THREE.Vector3 | null => {
    if (!meshes || meshes.length === 0) return null;
    const box = new THREE.Box3();
    meshes.forEach(mesh => {
        // Ensure mesh is valid before expanding box
        if (mesh && mesh.geometry) { 
             box.expandByObject(mesh); 
        }
    });
    if (box.isEmpty()) return null; // Return null if box couldn't be expanded
    const center = new THREE.Vector3();
    box.getCenter(center);
    return center;
};

// Types for animation targets
type AnimationTarget = {
    position?: THREE.Vector3;
    rotation?: THREE.Euler;
    scale?: THREE.Vector3;
    opacity?: number;
    color?: THREE.Color;
};

type AnimationStep = {
    target: AnimationTarget;
    duration: number;
    easing?: string;
    delay?: number;
    label?: string;
};

type KeyframeAnimation = {
    target: AnimationTarget;
    keyframes: Array<{
        value: any;
        time: number;
    }>;
    duration: number;
    easing?: string;
};

// Animation timeline class
class AnimationTimeline {
    private steps: Array<{
        target: AnimationTarget;
        duration: number;
        startTime: number;
        endTime: number;
        easing: string;
        onComplete?: () => void;
    }> = [];
    private isPlaying: boolean = false;
    private startTime: number = 0;
    private currentTime: number = 0;
    private animationFrameId: number | null = null;

    constructor() {
        this.steps = [];
        this.isPlaying = false;
        this.startTime = 0;
        this.currentTime = 0;
    }

    addStep(step: AnimationStep, onComplete?: () => void) {
        const startTime = this.steps.length > 0 
            ? this.steps[this.steps.length - 1].endTime + (step.delay || 0)
            : 0;
        
        this.steps.push({
            ...step,
            startTime,
            endTime: startTime + step.duration,
            easing: step.easing || 'easeInOutCubic',
            onComplete
        });
    }

    play() {
        if (this.isPlaying) return;
        
        this.isPlaying = true;
        this.startTime = performance.now();
        this.currentTime = 0;
        this.animate();
    }

    pause() {
        this.isPlaying = false;
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    private animate = () => {
        if (!this.isPlaying) return;

        this.currentTime = performance.now() - this.startTime;
        
        let allComplete = true;
        this.steps.forEach(step => {
            if (this.currentTime >= step.startTime && this.currentTime <= step.endTime) {
                const progress = (this.currentTime - step.startTime) / step.duration;
                const easedProgress = this.ease(progress, step.easing);
                
                // Update target properties
                Object.entries(step.target).forEach(([key, value]) => {
                    if (value instanceof THREE.Vector3) {
                        value.lerp(step.target[key] as THREE.Vector3, easedProgress);
                    } else if (value instanceof THREE.Euler) {
                        value.set(
                            THREE.MathUtils.lerp(value.x, (step.target[key] as THREE.Euler).x, easedProgress),
                            THREE.MathUtils.lerp(value.y, (step.target[key] as THREE.Euler).y, easedProgress),
                            THREE.MathUtils.lerp(value.z, (step.target[key] as THREE.Euler).z, easedProgress)
                        );
                    } else if (typeof value === 'number') {
                        step.target[key] = THREE.MathUtils.lerp(value, step.target[key] as number, easedProgress);
                    }
                });

                allComplete = false;
            } else if (this.currentTime > step.endTime && step.onComplete) {
                step.onComplete();
            }
        });

        if (!allComplete) {
            this.animationFrameId = requestAnimationFrame(this.animate);
        } else {
            this.isPlaying = false;
        }
    }

    private ease(t: number, easing: string): number {
        switch (easing) {
            case 'easeInOutCubic':
                return t < 0.5
                    ? 4 * t * t * t
                    : 1 - Math.pow(-2 * t + 2, 3) / 2;
            case 'easeInCubic':
                return t * t * t;
            case 'easeOutCubic':
                return 1 - Math.pow(1 - t, 3);
            default:
                return t;
        }
    }
}

// Define the structure for a scene
export interface SceneConfig {
  id: string;
  label: string;
  duration: number; // Default duration for SIMPLE transitions (still useful)
  cameraState: CameraState; // Default camera state (can be first step in steps)
  entryFX?: ( // Simple effects (can coexist or be replaced by steps)
    meshGroups: MeshGroupType,
    timeline: AnimeTimelineInstance
  ) => void;
  // Replaced animationSequence function with declarative steps
  animationSteps?: AnimationStep[]; // <<< NEW: Array of animation steps
  keyframeAnimations?: KeyframeObject[]; 
}

// Helper type guard for materials with opacity
function hasOpacity(material: THREE.Material): material is THREE.Material & { opacity: number } {
  return 'opacity' in material;
}

// Export scenes configuration
export const scenes: SceneConfig[] = [
    {
        id: 'initial-view',
        label: 'Initial View',
        duration: 1000,
        cameraState: {
            position: [0, 5, 10],
            target: [0, 0, 0]
        },
        animationSteps: [
            {
                target: {
                    position: new THREE.Vector3(0, 5, 10),
                    rotation: new THREE.Euler(0, 0, 0)
                },
                duration: 1000,
                easing: 'easeInOutCubic',
                label: 'Initial Position'
            }
        ]
    },
    {
        id: 'orbit-view',
        label: 'Orbit View',
        duration: 2000,
        cameraState: {
            position: [10, 5, 0],
            target: [0, 0, 0]
        },
        animationSteps: [
            {
                target: {
                    position: new THREE.Vector3(10, 5, 0),
                    rotation: new THREE.Euler(0, Math.PI / 2, 0)
                },
                duration: 2000,
                easing: 'easeInOutCubic',
                label: 'Orbit Position'
            }
        ]
    },
    {
        id: 'close-up',
        label: 'Close Up',
        duration: 1500,
        cameraState: {
            position: [0, 2, 5],
            target: [0, 0, 0]
        },
        animationSteps: [
            {
                target: {
                    position: new THREE.Vector3(0, 2, 5),
                    rotation: new THREE.Euler(-Math.PI / 6, 0, 0)
                },
                duration: 1500,
                easing: 'easeInOutCubic',
                label: 'Close Up Position'
            }
        ]
    }
];

// Helper function to get scene by ID
export const getSceneById = (id: string): SceneConfig | undefined => {
    return scenes.find(scene => scene.id === id);
}; 

// Build timeline from steps
export const buildTimelineFromSteps = (
    steps: AnimationStep[],
    camera: THREE.Camera,
    controls: typeof OrbitControls,
    meshGroups: any,
    onComplete?: () => void
): AnimationTimeline | null => {
    try {
        const timeline = new AnimationTimeline();
        
        steps.forEach((step, index) => {
            const isLastStep = index === steps.length - 1;
            
            timeline.addStep(step, isLastStep ? onComplete : undefined);
        });

        return timeline;
    } catch (error) {
        console.error('Error building timeline from steps:', error);
        return null;
    }
};

// Build timeline from keyframes
export const buildTimelineFromKeyframes = (
    keyframes: KeyframeAnimation[],
    camera: THREE.Camera,
    controls: typeof OrbitControls,
    meshGroups: any,
    onComplete?: () => void
): AnimationTimeline | null => {
    try {
        const timeline = new AnimationTimeline();
        
        keyframes.forEach((keyframe, index) => {
            const isLastKeyframe = index === keyframes.length - 1;
            
            timeline.addStep({
                target: keyframe.target,
                duration: keyframe.duration,
                easing: keyframe.easing
            }, isLastKeyframe ? onComplete : undefined);
        });

        return timeline;
    } catch (error) {
        console.error('Error building timeline from keyframes:', error);
        return null;
    }
}; 